import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["card", "search", "badge", "empty", "sheet", "overlay", "stateBtn", "classBtn", "gapsBtn", "distPct", "gapsCount", "productCount", "ph1Buttons", "ph2Buttons", "ph3Buttons", "ph2Section", "ph3Section"]

  connect() {
    // Sheet starts off-screen
    this.sheetTarget.style.transform       = "translateY(100%)"
    this.overlayTarget.style.opacity       = "0"
    this.overlayTarget.style.pointerEvents = "none"
    this.overlayTarget.style.backgroundColor = "rgba(0,0,0,0.6)"
    requestAnimationFrame(() => {
      this.sheetTarget.style.transition   = "transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)"
      this.overlayTarget.style.transition = "opacity 0.25s ease"
    })
    this._bindDrag()
    this._activeStates  = new Set()
    this._activeClasses = new Set()
    this._gapsMode = false
    this._activePh1 = new Set()
    this._activePh2 = new Set()
    this._activePh3 = new Set()
    this._buildPh1Options()
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.updateStats()
      this.updateProductCount()
    }))
  }

  disconnect() { this._unbindDrag() }

  openSheet() {
    this._transitionsOn()
    this.overlayTarget.style.pointerEvents = "auto"
    this.overlayTarget.style.opacity       = "1"
    this.sheetTarget.style.transform       = "translateY(0)"
  }

  closeSheet() {
    this._transitionsOn()
    this.overlayTarget.style.opacity       = "0"
    this.overlayTarget.style.pointerEvents = "none"
    this.sheetTarget.style.transform       = "translateY(100%)"
  }

  filter() {
    const q = this.searchTarget.value.toLowerCase().trim()
    let visibleCount = 0

    this.cardTargets.forEach(card => {
      const nameMatch  = !q || card.dataset.name.includes(q) || card.dataset.sku.includes(q)
      const stateMatch = this._activeStates.size === 0 || this._activeStates.has(card.dataset.state)
      const classMatch = this._activeClasses.size === 0 || [...this._activeClasses].some(c => card.dataset[c] === "true")

      const ph1Match = this._activePh1.size === 0 || this._activePh1.has(card.dataset.ph1)
      const ph2Match = this._activePh2.size === 0 || this._activePh2.has(card.dataset.ph2)
      const ph3Match = this._activePh3.size === 0 || this._activePh3.has(card.dataset.ph3)

      const visible = nameMatch && stateMatch && classMatch && ph1Match && ph2Match && ph3Match
      card.style.display = visible ? "" : "none"
      if (visible) visibleCount++
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visibleCount > 0)
    }
    this.updateProductCount(visibleCount)
    this._updateBadge()
  }

  toggleState(e) {
    this._gapsMode = false
    this._resetGapsBtn()
    const btn = e.currentTarget
    const val = btn.dataset.stateValue
    if (this._activeStates.has(val)) {
      this._activeStates.delete(val)
      btn.classList.remove("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.add("bg-white", "text-slate-500", "border-slate-200")
    } else {
      this._activeStates.add(val)
      btn.classList.add("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.remove("bg-white", "text-slate-500", "border-slate-200")
    }
    this.filter()
  }

  toggleClass(e) {
    this._gapsMode = false
    this._resetGapsBtn()
    const btn = e.currentTarget
    const val = btn.dataset.classValue
    if (this._activeClasses.has(val)) {
      this._activeClasses.delete(val)
      btn.classList.remove("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.add("bg-white", "text-slate-500", "border-slate-200")
    } else {
      this._activeClasses.add(val)
      btn.classList.add("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.remove("bg-white", "text-slate-500", "border-slate-200")
    }
    this.filter()
  }

  clearAll() {
    this._activeStates.clear()
    this._activeClasses.clear()
    this._gapsMode = false
    this._activePh1 = new Set()
    this._activePh2 = new Set()
    this._activePh3 = new Set()
    this._buildPh1Options()
    if (this.hasPh2SectionTarget) this.ph2SectionTarget.classList.add("hidden")
    if (this.hasPh3SectionTarget) this.ph3SectionTarget.classList.add("hidden")
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this.stateBtnTargets.forEach(btn => {
      btn.classList.remove("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.add("bg-white", "text-slate-500", "border-slate-200")
    })
    this.classBtnTargets.forEach(btn => {
      btn.classList.remove("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.add("bg-white", "text-slate-500", "border-slate-200")
    })
    if (this.hasGapsBtnTarget) {
      this.gapsBtnTarget.classList.remove("bg-sapphire", "border-sapphire")
      this.gapsBtnTarget.querySelector("svg").classList.add("text-amber-400")
      this.gapsBtnTarget.querySelector("svg").classList.remove("text-white")
    }
    this.filter()
    this.closeSheet()
  }

  toggleGaps() {
    if (this._gapsMode) {
      // Turn off — clear everything
      this.clearAll()
      return
    }

    // Turn on gaps mode: state = C + X, class = core + focus + npd
    this._gapsMode = true
    this._activeStates = new Set(["C", "X"])
    this._activeClasses = new Set(["core", "focus", "npd"])

    // Highlight state buttons C and X
    this.stateBtnTargets.forEach(btn => {
      const active = ["C", "X"].includes(btn.dataset.stateValue)
      btn.classList.toggle("bg-sapphire",      active)
      btn.classList.toggle("text-white",       active)
      btn.classList.toggle("border-sapphire",  active)
      btn.classList.toggle("bg-white",         !active)
      btn.classList.toggle("text-slate-500",   !active)
      btn.classList.toggle("border-slate-200", !active)
    })

    // Highlight all class buttons
    this.classBtnTargets.forEach(btn => {
      btn.classList.add("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.remove("bg-white", "text-slate-500", "border-slate-200")
    })

    // Highlight gaps button
    if (this.hasGapsBtnTarget) {
      this.gapsBtnTarget.classList.add("bg-sapphire", "border-sapphire")
      this.gapsBtnTarget.querySelector("svg").classList.remove("text-amber-400")
      this.gapsBtnTarget.querySelector("svg").classList.add("text-white")
    }

    this.filter()
  }

  updateStats() {
    const cards         = this.cardTargets
    const totalWithState = cards.filter(c => c.dataset.state !== "").length
    const ranged         = cards.filter(c => c.dataset.state === "I" || c.dataset.state === "O").length
    const distPct        = totalWithState > 0 ? Math.round(ranged / totalWithState * 100) : null
    const criticalGaps   = cards.filter(c =>
      (c.dataset.state === "C" || c.dataset.state === "X") &&
      (c.dataset.core === "true" || c.dataset.focus === "true" || c.dataset.npd === "true")
    ).length

    if (this.hasDistPctTarget)  this.distPctTarget.textContent  = distPct !== null ? `${distPct}%` : "—"
    if (this.hasGapsCountTarget) this.gapsCountTarget.textContent = String(criticalGaps)
  }

  updateProductCount(count) {
    const n = count !== undefined ? count : this.cardTargets.filter(c => c.style.display !== "none").length
    if (this.hasProductCountTarget) this.productCountTarget.textContent = String(n)
  }

  _buildPh1Options() {
    if (!this.hasPh1ButtonsTarget) return
    const values = [...new Set(
      this.cardTargets.map(c => c.dataset.ph1).filter(v => v && v.trim())
    )].sort()

    this.ph1ButtonsTarget.innerHTML = values.map(v =>
      `<button type="button"
               class="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-600 active:bg-slate-50"
               data-action="click->field-shelf-filter#togglePh1"
               data-ph-value="${v}">${v.charAt(0).toUpperCase() + v.slice(1)}</button>`
    ).join("")

    if (values.length === 0) {
      const section = this.ph1ButtonsTarget.parentElement
      if (section) section.classList.add("hidden")
    } else {
      const section = this.ph1ButtonsTarget.parentElement
      if (section) section.classList.remove("hidden")
    }
  }

  togglePh1(event) {
    const btn = event.currentTarget
    const value = btn.dataset.phValue
    this._gapsMode = false
    this._resetGapsBtn()

    if (this._activePh1.has(value)) {
      this._activePh1.delete(value)
      btn.classList.remove("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.add("bg-white", "text-slate-600", "border-slate-200")
    } else {
      this._activePh1.add(value)
      btn.classList.add("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.remove("bg-white", "text-slate-600", "border-slate-200")
    }

    this._activePh2 = new Set()
    this._activePh3 = new Set()
    this._updatePh2Options()
    this._updateBadge()
    this.filter()
  }

  _updatePh2Options() {
    if (!this.hasPh2SectionTarget) return
    const matchingCards = this._activePh1.size > 0
      ? this.cardTargets.filter(c => this._activePh1.has(c.dataset.ph1))
      : this.cardTargets

    const values = [...new Set(
      matchingCards.map(c => c.dataset.ph2).filter(v => v && v.trim())
    )].sort()

    if (values.length === 0) {
      this.ph2SectionTarget.classList.add("hidden")
      this.ph3SectionTarget.classList.add("hidden")
      return
    }

    this.ph2ButtonsTarget.innerHTML = values.map(v =>
      `<button type="button"
               class="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-600 active:bg-slate-50"
               data-action="click->field-shelf-filter#togglePh2"
               data-ph-value="${v}">${v.charAt(0).toUpperCase() + v.slice(1)}</button>`
    ).join("")
    this.ph2SectionTarget.classList.remove("hidden")
    this.ph3SectionTarget.classList.add("hidden")
  }

  togglePh2(event) {
    const btn = event.currentTarget
    const value = btn.dataset.phValue
    this._gapsMode = false
    this._resetGapsBtn()

    if (this._activePh2.has(value)) {
      this._activePh2.delete(value)
      btn.classList.remove("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.add("bg-white", "text-slate-600", "border-slate-200")
    } else {
      this._activePh2.add(value)
      btn.classList.add("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.remove("bg-white", "text-slate-600", "border-slate-200")
    }

    this._activePh3 = new Set()
    this._updatePh3Options()
    this.filter()
  }

  _updatePh3Options() {
    if (!this.hasPh3SectionTarget) return
    const matchingCards = this._activePh2.size > 0
      ? this.cardTargets.filter(c => this._activePh2.has(c.dataset.ph2))
      : this.cardTargets

    const values = [...new Set(
      matchingCards.map(c => c.dataset.ph3).filter(v => v && v.trim())
    )].sort()

    if (values.length === 0) {
      this.ph3SectionTarget.classList.add("hidden")
      return
    }

    this.ph3ButtonsTarget.innerHTML = values.map(v =>
      `<button type="button"
               class="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-600 active:bg-slate-50"
               data-action="click->field-shelf-filter#togglePh3"
               data-ph-value="${v}">${v.charAt(0).toUpperCase() + v.slice(1)}</button>`
    ).join("")
    this.ph3SectionTarget.classList.remove("hidden")
  }

  togglePh3(event) {
    const btn = event.currentTarget
    const value = btn.dataset.phValue
    this._gapsMode = false
    this._resetGapsBtn()

    if (this._activePh3.has(value)) {
      this._activePh3.delete(value)
      btn.classList.remove("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.add("bg-white", "text-slate-600", "border-slate-200")
    } else {
      this._activePh3.add(value)
      btn.classList.add("bg-sapphire", "text-white", "border-sapphire")
      btn.classList.remove("bg-white", "text-slate-600", "border-slate-200")
    }

    this.filter()
  }

  _resetGapsBtn() {
    if (this.hasGapsBtnTarget) {
      this.gapsBtnTarget.classList.remove("bg-sapphire", "border-sapphire")
      this.gapsBtnTarget.querySelector("svg").classList.add("text-amber-400")
      this.gapsBtnTarget.querySelector("svg").classList.remove("text-white")
    }
  }

  _updateBadge() {
    const hasFilter = this._activeStates.size > 0 || this._activeClasses.size > 0 || this._activePh1.size > 0
    if (this.hasBadgeTarget) this.badgeTarget.classList.toggle("hidden", !hasFilter)
  }

  // Drag-to-dismiss — identical pattern to place_filter_controller.js
  _bindDrag() {
    this._onTouchStart = this._touchStart.bind(this)
    this._onTouchMove  = this._touchMove.bind(this)
    this._onTouchEnd   = this._touchEnd.bind(this)
    this.sheetTarget.addEventListener("touchstart", this._onTouchStart, { passive: true })
    this.sheetTarget.addEventListener("touchmove",  this._onTouchMove,  { passive: false })
    this.sheetTarget.addEventListener("touchend",   this._onTouchEnd,   { passive: true })
  }

  _unbindDrag() {
    this.sheetTarget.removeEventListener("touchstart", this._onTouchStart)
    this.sheetTarget.removeEventListener("touchmove",  this._onTouchMove)
    this.sheetTarget.removeEventListener("touchend",   this._onTouchEnd)
  }

  _touchStart(e) {
    this._dragStartY   = e.touches[0].clientY
    this._dragLastY    = e.touches[0].clientY
    this._dragLastTime = Date.now()
    this._dragVelocity = 0
    this._dragging     = true
    this._transitionsOff()
  }

  _touchMove(e) {
    if (!this._dragging) return
    const y       = e.touches[0].clientY
    const delta   = y - this._dragStartY
    const clamped = delta > 0 ? delta : delta * 0.1
    const now     = Date.now()
    this._dragVelocity = (y - this._dragLastY) / (now - this._dragLastTime || 1)
    this._dragLastY    = y
    this._dragLastTime = now
    this.sheetTarget.style.transform = `translateY(${clamped}px)`
    const progress = Math.min(Math.max(clamped / (this.sheetTarget.offsetHeight || 400), 0), 1)
    this.overlayTarget.style.opacity = String(1 - progress)
    if (delta > 0) e.preventDefault()
  }

  _touchEnd() {
    if (!this._dragging) return
    this._dragging = false
    const sheetHeight   = this.sheetTarget.offsetHeight || 400
    const currentY      = new DOMMatrixReadOnly(getComputedStyle(this.sheetTarget).transform).m42
    const pastThreshold = currentY > sheetHeight * 0.4
    const fastFlick     = this._dragVelocity > 0.5
    this._transitionsOn()
    if (pastThreshold || fastFlick) {
      this.closeSheet()
    } else {
      this.sheetTarget.style.transform = "translateY(0)"
      this.overlayTarget.style.opacity = "1"
    }
  }

  _transitionsOn() {
    this.sheetTarget.style.transition   = "transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)"
    this.overlayTarget.style.transition = "opacity 0.25s ease"
  }

  _transitionsOff() {
    this.sheetTarget.style.transition   = "none"
    this.overlayTarget.style.transition = "none"
  }
}
